// ============================================================================
// Aesthetic OS — Mock Data Layer
// ----------------------------------------------------------------------------
// Tutti i dati sono mock. La struttura è pensata per essere sostituita 1:1 da
// query Supabase. I valori "baseline" rappresentano una finestra di 30 giorni
// e vengono scalati in base al range temporale selezionato.
// ============================================================================

import {
  Megaphone,
  ClipboardList,
  BarChart3,
  LineChart,
  Users,
  FileText,
  Calendar,
  Bot,
  StickyNote,
} from "lucide-react";

export const SCALE_BASE_DAYS = 30;

// ----------------------------------------------------------------------------
// UTENTI
// ----------------------------------------------------------------------------
export const USERS = [
  { id: "u_andrea", name: "Andrea", email: "andrea@aestheticdigital.it", password: "aesthetic", role: "super_admin", initials: "A", avatar: null },
  { id: "u_beatrice_brucoli", name: "Beatrice Brucoli", email: null, password: "aesthetic123", role: "team", initials: "BB", avatar: null },
  { id: "u_antonella_marangi", name: "Antonella Marangi", email: null, password: "aesthetic123", role: "team", initials: "AM", avatar: null },
];
export const userById = (id) => USERS.find((u) => u.id === id);

// ----------------------------------------------------------------------------
// MODULI DISPONIBILI
// ----------------------------------------------------------------------------
export const MODULES = [
  { key: "meta-ads", label: "Meta Ads", icon: Megaphone, available: true, description: "Performance campagne Meta" },
  { key: "reports", label: "Report Operativi", icon: ClipboardList, available: true, description: "Archivio report mensili" },
  { key: "google-ads", label: "Google Ads", icon: LineChart, available: false, description: "In arrivo" },
  { key: "analytics", label: "Analytics", icon: BarChart3, available: false, description: "In arrivo" },
  { key: "crm", label: "CRM", icon: Users, available: false, description: "In arrivo" },
  { key: "documents", label: "Documenti", icon: FileText, available: false, description: "In arrivo" },
  { key: "calendar", label: "Calendario", icon: Calendar, available: false, description: "In arrivo" },
  { key: "ai", label: "AI Assistant", icon: Bot, available: false, description: "In arrivo" },
  { key: "notes", label: "Note", icon: StickyNote, available: false, description: "In arrivo" },
];

const CREATIVE = "https://images.pexels.com/photos/8015871/pexels-photo-8015871.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

// ----------------------------------------------------------------------------
// ALERT CATALOG  (un cliente può avere più alert simultanei)
// ----------------------------------------------------------------------------
export const ALERT_CATALOG = {
  ad_account_disabled: { label: "Account pubblicitario disabilitato", severity: "error" },
  payment_failed: { label: "Metodo di pagamento fallito", severity: "error" },
  not_delivering: { label: "Campagna non in erogazione", severity: "error" },
  ad_rejected: { label: "Inserzione rifiutata", severity: "error" },
  budget_exhausted: { label: "Budget esaurito", severity: "warning" },
  learning_limited: { label: "Learning limitato", severity: "warning" },
  cpl_above: { label: "CPL sopra soglia", severity: "warning" },
  cpa_above: { label: "CPA sopra soglia", severity: "warning" },
};

export const alertList = (codes = []) =>
  codes.map((c) => ({ code: c, ...ALERT_CATALOG[c] }));

// stato derivato dagli alert: error -> rosso, warning -> giallo, nessuno -> verde
export const statusFromAlerts = (codes = []) => {
  const sev = codes.map((c) => ALERT_CATALOG[c]?.severity);
  if (sev.includes("error")) return "error";
  if (sev.includes("warning")) return "warning";
  return "ok";
};

export const CLIENT_STATUS = {
  ok: { label: "OK", tone: "ok" },
  warning: { label: "Attenzione", tone: "warning" },
  error: { label: "Problema", tone: "error" },
};

// ----------------------------------------------------------------------------
// HELPER KPI
// ----------------------------------------------------------------------------
const kpis = ({ spesa, lead, impression, reach, ctr, cpm }) => ({
  spesa,
  lead,
  cpl: lead ? +(spesa / lead).toFixed(2) : 0,
  ctr,
  cpm,
  impression,
  reach,
  frequenza: reach ? +(impression / reach).toFixed(2) : 0,
});

const ad = (id, name, status, k) => ({ id, name, status, creative: CREATIVE, kpis: kpis(k) });

const adSet = (id, name, ads) => ({
  id,
  name,
  ads,
  kpis: kpis({
    spesa: ads.reduce((s, a) => s + a.kpis.spesa, 0),
    lead: ads.reduce((s, a) => s + a.kpis.lead, 0),
    impression: ads.reduce((s, a) => s + a.kpis.impression, 0),
    reach: ads.reduce((s, a) => s + a.kpis.reach, 0),
    ctr: +(ads.reduce((s, a) => s + a.kpis.ctr, 0) / ads.length).toFixed(2),
    cpm: +(ads.reduce((s, a) => s + a.kpis.cpm, 0) / ads.length).toFixed(2),
  }),
});

const campaign = (id, name, status, budget, watchTime, adSets) => ({
  id,
  name,
  status,
  budget,
  watchTime, // secondi medi di visualizzazione
  adSets,
  kpis: kpis({
    spesa: adSets.reduce((s, a) => s + a.kpis.spesa, 0),
    lead: adSets.reduce((s, a) => s + a.kpis.lead, 0),
    impression: adSets.reduce((s, a) => s + a.kpis.impression, 0),
    reach: adSets.reduce((s, a) => s + a.kpis.reach, 0),
    ctr: +(adSets.reduce((s, a) => s + a.kpis.ctr, 0) / adSets.length).toFixed(2),
    cpm: +(adSets.reduce((s, a) => s + a.kpis.cpm, 0) / adSets.length).toFixed(2),
  }),
});

const sumCampaigns = (campaigns) =>
  kpis({
    spesa: campaigns.reduce((s, c) => s + c.kpis.spesa, 0),
    lead: campaigns.reduce((s, c) => s + c.kpis.lead, 0),
    impression: campaigns.reduce((s, c) => s + c.kpis.impression, 0),
    reach: campaigns.reduce((s, c) => s + c.kpis.reach, 0),
    ctr: +(campaigns.reduce((s, c) => s + c.kpis.ctr, 0) / campaigns.length).toFixed(2),
    cpm: +(campaigns.reduce((s, c) => s + c.kpis.cpm, 0) / campaigns.length).toFixed(2),
  });

const buildMeta = (seed) => {
  const c1 = campaign(`${seed}_c1`, "Lead Generation — Sempre Attiva", "active", 1500, 24, [
    adSet(`${seed}_c1_a1`, "Pubblico Freddo · Interessi", [
      ad(`${seed}_c1_a1_1`, "Video Hook 15s", "active", { spesa: 420, lead: 38, impression: 92000, reach: 61000, ctr: 1.8, cpm: 4.6 }),
      ad(`${seed}_c1_a1_2`, "Carosello Benefici", "active", { spesa: 310, lead: 24, impression: 71000, reach: 49000, ctr: 1.4, cpm: 4.4 }),
      ad(`${seed}_c1_a1_3`, "Immagine Statica UGC", "paused", { spesa: 180, lead: 9, impression: 38000, reach: 30000, ctr: 0.9, cpm: 4.7 }),
    ]),
    adSet(`${seed}_c1_a2`, "Retargeting · Visitatori 30gg", [
      ad(`${seed}_c1_a2_1`, "Offerta Limitata", "active", { spesa: 260, lead: 31, impression: 41000, reach: 22000, ctr: 3.1, cpm: 6.3 }),
      ad(`${seed}_c1_a2_2`, "Testimonianza Cliente", "active", { spesa: 190, lead: 21, impression: 33000, reach: 18000, ctr: 2.7, cpm: 5.7 }),
    ]),
  ]);
  const c2 = campaign(`${seed}_c2`, "Awareness — Brand", "active", 800, 31, [
    adSet(`${seed}_c2_a1`, "Reach · Geo Locale", [
      ad(`${seed}_c2_a1_1`, "Spot Brand 30s", "active", { spesa: 340, lead: 6, impression: 188000, reach: 142000, ctr: 0.6, cpm: 1.8 }),
      ad(`${seed}_c2_a1_2`, "Manifesto Valori", "active", { spesa: 220, lead: 4, impression: 121000, reach: 98000, ctr: 0.5, cpm: 1.8 }),
    ]),
  ]);
  const c3 = campaign(`${seed}_c3`, "Conversione — Promo Stagionale", "paused", 1200, 14, [
    adSet(`${seed}_c3_a1`, "Lookalike 1% Acquirenti", [
      ad(`${seed}_c3_a1_1`, "Bundle Sconto 20%", "paused", { spesa: 510, lead: 44, impression: 76000, reach: 52000, ctr: 2.2, cpm: 6.7 }),
      ad(`${seed}_c3_a1_2`, "Countdown Urgency", "paused", { spesa: 280, lead: 19, impression: 48000, reach: 34000, ctr: 1.6, cpm: 5.8 }),
    ]),
  ]);
  const campaigns = [c1, c2, c3];
  return { campaigns, kpis: sumCampaigns(campaigns) };
};

// ----------------------------------------------------------------------------
// TASK
// ----------------------------------------------------------------------------
const buildTasks = (seed) => [
  { id: `${seed}_t1`, title: "Nuove creatività video per campagna Lead Gen", description: "Produrre 3 hook video da 15s in formato 9:16 per il pubblico freddo.", status: "in_progress", priority: "high", assignee: "u_antonella", due: "2026-06-18", createdBy: "u_andrea", notes: "Brief condiviso su Drive. Tone of voice premium." },
  { id: `${seed}_t2`, title: "Report mensile performance", description: "Esportare KPI di maggio e preparare slide per il cliente.", status: "todo", priority: "medium", assignee: "u_beatrice", due: "2026-06-30", createdBy: "u_andrea", notes: "" },
  { id: `${seed}_t3`, title: "Setup pixel eventi conversione", description: "Verificare tracciamento Lead e Purchase via CAPI.", status: "waiting", priority: "high", assignee: "u_roberta", due: "2026-06-15", createdBy: "u_beatrice", notes: "In attesa accessi developer del cliente." },
  { id: `${seed}_t4`, title: "Ottimizzazione budget retargeting", description: "Spostare il 15% del budget verso l'ad set retargeting più performante.", status: "completed", priority: "low", assignee: "u_antonella", due: "2026-06-10", createdBy: "u_andrea", notes: "Fatto, CPL sceso del 12%." },
  { id: `${seed}_t5`, title: "Revisione copy landing page", description: "Allineare il copy della landing alla nuova promo stagionale.", status: "todo", priority: "medium", assignee: "u_roberta", due: "2026-06-22", createdBy: "u_beatrice", notes: "" },
  { id: `${seed}_t6`, title: "Call settimanale di allineamento", description: "Preparare agenda e punti aperti per la call del lunedì.", status: "in_progress", priority: "low", assignee: "u_beatrice", due: "2026-06-16", createdBy: "u_andrea", notes: "" },
];

// ----------------------------------------------------------------------------
// CLIENTI  (rates = tassi di funnel per derivare le KPI di conversione)
// ----------------------------------------------------------------------------
const rawClients = [
  { id: "c_velluto", name: "Velluto Atelier", industry: "Moda & Lusso", manager: "u_beatrice", seed: "velluto", alerts: [], rates: { appt: 0.42, managed: 0.74, programs: 0.5, noShow: 0.12, wrong: 0.06, outside: 0.04, notInterested: 0.18 } },
  { id: "c_aurora", name: "Aurora Medical Spa", industry: "Beauty & Wellness", manager: "u_antonella", seed: "aurora", alerts: ["learning_limited", "cpl_above"], rates: { appt: 0.38, managed: 0.62, programs: 0.44, noShow: 0.18, wrong: 0.09, outside: 0.06, notInterested: 0.24 } },
  { id: "c_nordfit", name: "NordFit Studios", industry: "Fitness", manager: "u_roberta", seed: "nordfit", alerts: [], rates: { appt: 0.46, managed: 0.8, programs: 0.52, noShow: 0.1, wrong: 0.05, outside: 0.03, notInterested: 0.16 } },
  { id: "c_casaviva", name: "CasaViva Arredi", industry: "Arredamento", manager: "u_beatrice", seed: "casaviva", alerts: ["ad_rejected", "payment_failed", "cpl_above"], rates: { appt: 0.3, managed: 0.5, programs: 0.4, noShow: 0.22, wrong: 0.12, outside: 0.08, notInterested: 0.28 } },
  { id: "c_gusto", name: "Gusto Bistrot", industry: "Food & Beverage", manager: "u_antonella", seed: "gusto", alerts: [], rates: { appt: 0.4, managed: 0.7, programs: 0.48, noShow: 0.14, wrong: 0.07, outside: 0.05, notInterested: 0.2 } },
  { id: "c_lumen", name: "Lumen Dental", industry: "Healthcare", manager: "u_roberta", seed: "lumen", alerts: ["budget_exhausted"], rates: { appt: 0.36, managed: 0.58, programs: 0.46, noShow: 0.16, wrong: 0.08, outside: 0.05, notInterested: 0.22 } },
];

export const CLIENTS = rawClients.map((c, i) => {
  const meta = buildMeta(c.seed);
  const tasks = buildTasks(c.seed);
  return {
    ...c,
    initials: c.name.split(" ").map((w) => w[0]).slice(0, 2).join(""),
    status: statusFromAlerts(c.alerts),
    lastUpdate: "2 min fa",
    activeCampaigns: meta.campaigns.filter((x) => x.status === "active").length,
    adAccountId: `act_${4015567200 + i * 137}`,
    meta,
    tasks,
  };
});

export const clientById = (id) => CLIENTS.find((c) => c.id === id);

// ----------------------------------------------------------------------------
// UTENTI CLIENTE (ruolo client_user) — accedono solo al Client Portal e vedono
// esclusivamente i lead del proprio client_id. Futuro: Supabase Auth + RLS.
// ----------------------------------------------------------------------------
export const CLIENT_USERS = [];
export const ALL_LOGIN_USERS = [...USERS, ...CLIENT_USERS];
export const loginUserBy = (email, password) =>
  ALL_LOGIN_USERS.find(
    (u) => u.email.toLowerCase() === email.trim().toLowerCase() && u.password === password
  );

// ----------------------------------------------------------------------------
// METRICHE SCALATE PER RANGE TEMPORALE
// ----------------------------------------------------------------------------
const f = (days) => days / SCALE_BASE_DAYS;
const r = (n) => Math.round(n);

export const clientMetrics = (client, days = SCALE_BASE_DAYS) => {
  const factor = f(days);
  const spesa = +(client.meta.kpis.spesa * factor).toFixed(2);
  const leads = r(client.meta.kpis.lead * factor);
  const appointments = r(leads * client.rates.appt);
  return {
    spesa,
    leads,
    appointments,
    cpl: leads ? +(spesa / leads).toFixed(2) : 0,
    activeCampaigns: client.activeCampaigns,
  };
};

export const campaignMetrics = (campaign, days = SCALE_BASE_DAYS) => {
  const factor = f(days);
  const spesa = +(campaign.kpis.spesa * factor).toFixed(2);
  const leads = r(campaign.kpis.lead * factor);
  return {
    spesa,
    leads,
    cpl: leads ? +(spesa / leads).toFixed(2) : 0,
    impression: r(campaign.kpis.impression * factor),
    reach: r(campaign.kpis.reach * factor),
    watchTime: campaign.watchTime,
    dailyBudget: campaign.budget,
  };
};

export const campaignFunnel = (campaign, client, days = SCALE_BASE_DAYS) => {
  const factor = f(days);
  const spesa = +(campaign.kpis.spesa * factor).toFixed(2);
  const leads = r(campaign.kpis.lead * factor);
  const rt = client.rates;
  const appointments = r(leads * rt.appt);
  return {
    leads,
    appointments,
    programsSold: r(appointments * rt.programs),
    noShow: r(appointments * rt.noShow),
    wrongNumbers: r(leads * rt.wrong),
    outsideServiceArea: r(leads * rt.outside),
    notInterested: r(leads * rt.notInterested),
    cpl: leads ? +(spesa / leads).toFixed(2) : 0,
    cpa: appointments ? +(spesa / appointments).toFixed(2) : 0,
    spesa,
  };
};

// Metriche a livello inserzione (Leads, CPL, Spesa, Reach, Impression).
// Appuntamenti / contatti gestiti restano SOLO a livello campagna.
export const adMetrics = (ad, client, days = SCALE_BASE_DAYS) => {
  const factor = f(days);
  const leads = r(ad.kpis.lead * factor);
  const spesa = +(ad.kpis.spesa * factor).toFixed(2);
  return {
    leads,
    spesa,
    cpl: leads ? +(spesa / leads).toFixed(2) : 0,
    reach: r(ad.kpis.reach * factor),
    impression: r(ad.kpis.impression * factor),
  };
};

// Tutte le inserzioni di una campagna (appiattite dagli ad set).
export const campaignAds = (campaign) => campaign.adSets.flatMap((s) => s.ads);

// Aggregato Meta Ads a livello cliente (scalato per periodo).
export const clientMeta = (client, days = SCALE_BASE_DAYS) => {
  const factor = f(days);
  const k = client.meta.kpis;
  const spesa = +(k.spesa * factor).toFixed(2);
  const leads = r(k.lead * factor);
  return {
    spesa,
    leads,
    cpl: leads ? +(spesa / leads).toFixed(2) : 0,
    impression: r(k.impression * factor),
    reach: r(k.reach * factor),
    ctr: k.ctr,
    cpm: k.cpm,
    activeCampaigns: client.activeCampaigns,
  };
};

// Operational Funnel Metrics (provenienti dal Google Sheet del cliente).
// Rappresentano l'esito reale dei lead generati.
export const clientFunnel = (client, days = SCALE_BASE_DAYS) => {
  const factor = f(days);
  const leads = r(client.meta.kpis.lead * factor);
  const rt = client.rates;
  const appointments = r(leads * rt.appt);
  const managedContacts = Math.min(leads, r(leads * rt.managed));
  return {
    leads,
    appointmentsBooked: appointments,
    programsSold: r(appointments * rt.programs),
    noShow: r(appointments * rt.noShow),
    wrongNumbers: r(leads * rt.wrong),
    outsideServiceArea: r(leads * rt.outside),
    notInterested: r(leads * rt.notInterested),
    managedContacts,
    contactsToManage: Math.max(0, leads - managedContacts),
  };
};

export const agencyStats = (days = SCALE_BASE_DAYS) => {
  const rows = CLIENTS.map((c) => clientMetrics(c, days));
  const spesa = +rows.reduce((s, m) => s + m.spesa, 0).toFixed(2);
  const leads = rows.reduce((s, m) => s + m.leads, 0);
  return {
    clients: CLIENTS.length,
    activeCampaigns: rows.reduce((s, m) => s + m.activeCampaigns, 0),
    spesa,
    leads,
    appointments: rows.reduce((s, m) => s + m.appointments, 0),
    avgCpl: leads ? +(spesa / leads).toFixed(2) : 0,
  };
};

// ----------------------------------------------------------------------------
// LABEL stati task / inserzioni
// ----------------------------------------------------------------------------
export const TASK_STATUS = {
  todo: { label: "To Do", tone: "neutral" },
  in_progress: { label: "In Progress", tone: "info" },
  waiting: { label: "Waiting", tone: "warning" },
  completed: { label: "Completed", tone: "ok" },
};
export const TASK_PRIORITY = {
  high: { label: "Alta", tone: "error" },
  medium: { label: "Media", tone: "warning" },
  low: { label: "Bassa", tone: "neutral" },
};
export const AD_STATUS = {
  active: { label: "Attiva", tone: "ok" },
  paused: { label: "In pausa", tone: "neutral" },
};
